import React, {
  createContext,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react'

const MotionContext = createContext({
  documentVisible: false,
  motionEnabled: false,
  loopEnabled: false,
})

export const TwentyTwoMotionProvider = ({ children }) => {
  // Match the server on the first client render; enable motion after hydration.
  const [settings, setSettings] = useState({
    documentVisible: false,
    motionEnabled: false,
    loopEnabled: false,
  })

  useEffect(() => {
    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)')
    const desktop = window.matchMedia(
      '(min-width: 621px) and (hover: hover) and (pointer: fine)'
    )
    const sync = () => {
      const next = {
        documentVisible: document.visibilityState !== 'hidden',
        motionEnabled: !reduced.matches,
        loopEnabled: desktop.matches && !reduced.matches,
      }
      setSettings((previous) =>
        Object.keys(next).every((key) => previous[key] === next[key])
          ? previous
          : next
      )
    }

    const queries = [reduced, desktop]
    queries.forEach((query) => {
      if (query.addEventListener) query.addEventListener('change', sync)
      else query.addListener(sync)
    })
    document.addEventListener('visibilitychange', sync)
    sync()
    return () => {
      queries.forEach((query) => {
        if (query.removeEventListener) query.removeEventListener('change', sync)
        else query.removeListener(sync)
      })
      document.removeEventListener('visibilitychange', sync)
    }
  }, [])

  return (
    <MotionContext.Provider value={settings}>{children}</MotionContext.Provider>
  )
}

export const useTwentyTwoMotion = () => useContext(MotionContext)

// Share an observer between all rows instead of registering one per card flow.
const observers = new Map()

export const useTwentyTwoViewport = (rootMargin = '0px') => {
  const ref = useRef(null)
  const [isNearViewport, setIsNearViewport] = useState(false)

  useEffect(() => {
    const element = ref.current
    if (!element) return undefined
    if (!window.IntersectionObserver) {
      setIsNearViewport(true)
      return undefined
    }

    let group = observers.get(rootMargin)
    if (!group) {
      const targets = new Map()
      const observer = new window.IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            const update = targets.get(entry.target)
            if (update) update(entry.isIntersecting)
          })
        },
        { rootMargin }
      )
      group = { targets, observer }
      observers.set(rootMargin, group)
    }
    group.targets.set(element, setIsNearViewport)
    group.observer.observe(element)
    return () => {
      group.observer.unobserve(element)
      group.targets.delete(element)
      if (!group.targets.size) {
        group.observer.disconnect()
        observers.delete(rootMargin)
      }
    }
  }, [rootMargin])

  return { ref, isNearViewport }
}
